import type { CompleteGameData, itemId, Drop } from './types'

// Standard merge recipes - 5 items = 2 next tier, 3 items = 1 next tier
const STANDARD_MERGE = [
    { count: 5, drops: { next: 2 } },
    { count: 3, drops: { next: 1 } }
] as const satisfies readonly {
    count: number
    drops: Drop
}[]

// Premium merge - better ratios for higher tiers
const PREMIUM_MERGE = [
    { count: 5, drops: { next: 3 } },
    { count: 3, drops: { next: 2 } }
] as const satisfies readonly {
    count: number
    drops: Drop
}[]

// Charge recipe helper - creates a charge recipe with optional auto-spawn settings
type ChargeRecipe = {
    charge: number
    drops: Drop
    autoSpawn?: boolean
    dropRange?: number
    triggerMerge?: boolean
}

/** Manual tap only (tier 1 behavior) */
const tap = (charge: number, drops: Drop): ChargeRecipe => ({ charge, drops })

/** Auto-spawn when charged (tier 2 behavior, euclidean distance) */
const auto = (charge: number, drops: Drop, dropRange = 2): ChargeRecipe => ({
    charge, drops, autoSpawn: true, dropRange
})

/** Auto-spawn + trigger merge check (tier 3 behavior, euclidean distance) */
const autoMerge = (charge: number, drops: Drop, dropRange = 2): ChargeRecipe => ({
    charge, drops, autoSpawn: true, dropRange, triggerMerge: true
})

export const defaultGameData: CompleteGameData = {
    items: {
        tile: { id: 'tile' },

        // ===== BASIC MERGE CHAINS =====
        
        // Orb chain - explode on shade contact or tap, power: 10, 32, 100, 320, 1000
        'orb-1': {
            id: 'orb-1', display: { emoji: '💧' },
            chain: { id: 'orb', tier: 1, next: 'orb-2' },
            merge: { recipes: [...STANDARD_MERGE] },
            shade: { explodeOnShade: { power: 10, radius: 1 } }
        },
        'orb-2': {
            id: 'orb-2', display: { emoji: '💦' },
            chain: { id: 'orb', tier: 2, next: 'orb-3' },
            merge: { recipes: [...STANDARD_MERGE] },
            shade: { explodeOnShade: { power: 32, radius: 1 } }
        },
        'orb-3': {
            id: 'orb-3', display: { emoji: '🌊' },
            chain: { id: 'orb', tier: 3, next: 'orb-4' },
            merge: { recipes: [...STANDARD_MERGE] },
            shade: { explodeOnShade: { power: 100, radius: 1.5 } }
        },
        'orb-4': {
            id: 'orb-4', display: { emoji: '🌀' },
            chain: { id: 'orb', tier: 4, next: 'orb-5' },
            merge: { recipes: [...STANDARD_MERGE] },
            shade: { explodeOnShade: { power: 320, radius: 1.5 } }
        },
        'orb-5': {
            id: 'orb-5', display: { emoji: '🔵' },
            chain: { id: 'orb', tier: 5, next: null },
            // merge: { recipes: [...STANDARD_MERGE] },
            shade: { explodeOnShade: { power: 1000, radius: 2 } }
        },

        // Gem chain - premium merge, redrop overflow
        'gem-1': {
            id: 'gem-1', display: { emoji: '💎' },
            chain: { id: 'gem', tier: 1, next: 'gem-2' },
            merge: { recipes: [...PREMIUM_MERGE] }
        },
        'gem-2': {
            id: 'gem-2', display: { emoji: '💠' },
            chain: { id: 'gem', tier: 2, next: 'gem-3' },
            merge: { recipes: [...PREMIUM_MERGE] }
        },
        'gem-3': {
            id: 'gem-3', display: { emoji: '🔷' },
            chain: { id: 'gem', tier: 3, next: null },
            merge: { recipes: [...PREMIUM_MERGE] }
        },

        // ===== TAP WITH EXHAUST =====

        // Chest - exhausts HP, drops sequential items
        'chest-1': {
            id: 'chest-1', display: { emoji: '📦' },
            tap: {
                recipes: [
                    { charge: null, exhaust: 1, drops: {} }
                ]
            },
            exhaust: {
                drops: Array(50).fill({ 'orb-1': 1 })
            }
        },
        'chest-2': {
            id: 'chest-2', display: { emoji: '📦' },
            tap: {
                recipes: [
                    { charge: null, exhaust: 1, drops: {} }
                ]
            },
            exhaust: {
                drops: [
                    { 'orb-1': 50 }
                ]
            }
        },
        'chest-3': {
            id: 'chest-3', display: { emoji: '🎁' },
            tap: {
                recipes: [
                    { charge: null, exhaust: 1, drops: {} }
                ]
            },
            exhaust: {
                drops: [
                    { 'orb-1': 10 }
                ],
                triggerMerge: true
            }
        },

        // ===== CHARGE WITH RECIPES =====

        // Flower - charges over time, paused in shade
        // Tier 1: manual tap only, unlimited range
        'flower-1': {
            id: 'flower-1', display: { emoji: '🌱' },
            chain: { id: 'flower', tier: 1, next: 'flower-2' },
            merge: { recipes: [...STANDARD_MERGE] },
            shade: { pausedInShade: true },
            tap: {
                recipes: [
                    { charge: 180, exhaust: null, drops: { 'orb-1': 1 } }
                ]
            },
            charge: {
                // 0.33 orb-1/s
                max: 180,
                recipes: [
                    tap(180, { 'orb-1': 1 }),
                ]
            }
        },
        // Tier 2: auto-spawn at 90, manual tap 1/2/3 (unlimited range), up to 3 charges
        'flower-2': {
            id: 'flower-2', display: { emoji: '🌸' },
            chain: { id: 'flower', tier: 2, next: 'flower-3' },
            merge: { recipes: [...STANDARD_MERGE] },
            shade: { pausedInShade: true },
            tap: {
                recipes: [
                    { charge: 90, exhaust: null, drops: { 'orb-1': 1 } },
                    { charge: 180, exhaust: null, drops: { 'orb-1': 2 } },
                    { charge: 270, exhaust: null, drops: { 'orb-1': 3 } }
                ]
            },
            charge: {
                // 0.66 orb-1/s, 2x flower-1
                max: 270,
                recipes: [
                    auto(90, { 'orb-1': 1 }, 1),  // auto-spawn at 90 charge, short range
                ]
            },
        },
        // Tier 3: 2x flower-2 production, drops orb-2, 1 charge cap
        'flower-3': {
            id: 'flower-3', display: { emoji: '🌺' },
            chain: { id: 'flower', tier: 3, next: null },
            shade: { pausedInShade: true },
            tap: {
                recipes: [
                    { charge: 45, exhaust: null, drops: { 'orb-2': 1 } }
                ]
            },
            charge: {
                // 1.33 orb-2/s = 3.33 orb-1/s, 2x flower-2 speed
                max: 45,
                recipes: [
                    auto(45, { 'orb-2': 1 }, 1.5),  // auto-spawn at 45 charge, 8 tiles (range 1.5)
                ]
            },
        },

        // ===== TAP WITH CHARGE REQUIREMENTS =====

        // Crystal - requires charge to tap, gives resources
        'crystal-1': {
            id: 'crystal-1', display: { emoji: '🔮' },
            chain: { id: 'crystal', tier: 1, next: 'crystal-2' },
            merge: { recipes: [...STANDARD_MERGE] },
            tap: {
                recipes: [
                    { charge: 120, exhaust: null, drops: { crystalEnergy: 5 } },
                    { charge: 240, exhaust: null, drops: { crystalEnergy: 15 } }
                ]
            },
            charge: {
                max: 240,
                recipes: [
                    { charge: 120, drops: { crystalEnergy: 5 } },
                    { charge: 240, drops: { crystalEnergy: 15 } }
                ]
            }
        },
        'crystal-2': {
            id: 'crystal-2', display: { emoji: '💠' },
            chain: { id: 'crystal', tier: 2, next: 'crystal-3' },
            merge: { recipes: [...STANDARD_MERGE] },
            tap: {
                recipes: [
                    { charge: 54, exhaust: null, drops: { crystalEnergy: 10 } },
                    { charge: 126, exhaust: null, drops: { crystalEnergy: 25 } },
                    { charge: 180, exhaust: null, drops: { crystalEnergy: 50, 'gem-1': 1 } }
                ]
            },
            charge: {
                max: 180,
                recipes: [
                    { charge: 54, drops: { crystalEnergy: 10 } },
                    { charge: 126, drops: { crystalEnergy: 25 } },
                    { charge: 180, drops: { crystalEnergy: 50, 'gem-1': 1 } }
                ]
            }
        },
        'crystal-3': {
            id: 'crystal-3', display: { emoji: '⚡' },
            chain: { id: 'crystal', tier: 3, next: null },
            tap: {
                recipes: [
                    { charge: 24, exhaust: null, drops: { crystalEnergy: 20 } },
                    { charge: 60, exhaust: null, drops: { crystalEnergy: 50, 'gem-2': 1 } },
                    { charge: 120, exhaust: null, drops: { crystalEnergy: 100, 'gem-2': 2 } }
                ]
            },
            charge: {
                max: 120,
                recipes: [
                    { charge: 24, drops: { crystalEnergy: 20 } },
                    { charge: 60, drops: { crystalEnergy: 50, 'gem-2': 1 } },
                    { charge: 120, drops: { crystalEnergy: 100, 'gem-2': 2 } }
                ]
            }
        },

        // ===== COMBINED: CHARGE + EXHAUST =====

        // Treasure chest - charges up, then exhausts when tapped
        'treasure-1': {
            id: 'treasure-1', display: { emoji: '🗝️' },
            chain: { id: 'treasure', tier: 1, next: 'treasure-2' },
            merge: { recipes: [...STANDARD_MERGE] },
            tap: {
                recipes: [
                    { charge: 300, exhaust: 1, drops: {} }
                ]
            },
            exhaust: {
                drops: [
                    { gold: 10, 'gem-1': 1 },
                    { gold: 25, 'gem-1': 2 },
                    { gold: 50, 'gem-2': 1 }
                ]
            },
            charge: {
                max: 300,
                recipes: [
                    { charge: 300, drops: {} }
                ]
            }
        },
        'treasure-2': {
            id: 'treasure-2', display: { emoji: '💍' },
            chain: { id: 'treasure', tier: 2, next: 'treasure-3' },
            merge: { recipes: [...STANDARD_MERGE] },
            tap: {
                recipes: [
                    { charge: 120, exhaust: null, drops: { gold: 5 } },
                    { charge: 240, exhaust: 1, drops: {} }
                ]
            },
            exhaust: {
                drops: [
                    { gold: 50, 'gem-2': 1 },
                    { gold: 100, 'gem-2': 2 },
                    { gold: 200, 'gem-3': 1 }
                ]
            },
            charge: {
                max: 240,
                recipes: [
                    { charge: 120, drops: { gold: 5 } },
                    { charge: 240, drops: {} }
                ]
            }
        },
        'treasure-3': {
            id: 'treasure-3', display: { emoji: '👑' },
            chain: { id: 'treasure', tier: 3, next: null },
            tap: {
                recipes: [
                    { charge: 54, exhaust: null, drops: { gold: 10, crystalEnergy: 5 } },
                    { charge: 126, exhaust: null, drops: { gold: 25, crystalEnergy: 10 } },
                    { charge: 180, exhaust: 1, drops: {} }
                ]
            },
            exhaust: {
                drops: [
                    { gold: 200, 'gem-3': 1, crystalEnergy: 20 },
                    { gold: 500, 'gem-3': 2, crystalEnergy: 50 },
                    { gold: 1000, 'gem-3': 3, crystalEnergy: 100 }
                ]
            },
            charge: {
                max: 180,
                recipes: [
                    { charge: 54, drops: { gold: 10, crystalEnergy: 5 } },
                    { charge: 126, drops: { gold: 25, crystalEnergy: 10 } },
                    { charge: 180, drops: {} }
                ]
            }
        },

        // ===== SIMPLE TAP (NO CHARGE, NO EXHAUST) =====

        // Coin - instant tap, gives resources
        'coin-1': {
            id: 'coin-1', display: { emoji: '🪙' },
            chain: { id: 'coin', tier: 1, next: 'coin-2' },
            merge: { recipes: [...STANDARD_MERGE] },
            tap: {
                recipes: [
                    { charge: null, exhaust: null, drops: { gold: 1 } }
                ]
            }
        },
        'coin-2': {
            id: 'coin-2', display: { emoji: '💰' },
            chain: { id: 'coin', tier: 2, next: 'coin-3' },
            merge: { recipes: [...STANDARD_MERGE] },
            tap: {
                recipes: [
                    { charge: null, exhaust: null, drops: { gold: 5 } }
                ]
            }
        },
        'coin-3': {
            id: 'coin-3', display: { emoji: '💵' },
            chain: { id: 'coin', tier: 3, next: null },
            tap: {
                recipes: [
                    { charge: null, exhaust: null, drops: { gold: 25 } }
                ]
            }
        },

        // ===== MULTI-TILE ITEMS =====

        // Large structures - take up multiple tiles
        'castle-1': {
            id: 'castle-1', display: { emoji: '🏰' },
            size: { x: 2, y: 2 },
            chain: { id: 'castle', tier: 1, next: 'castle-2' },
            merge: { recipes: [...STANDARD_MERGE] },
            tap: {
                recipes: [
                    { charge: null, exhaust: null, drops: { gold: 5 } }
                ]
            }
        },
        'castle-2': {
            id: 'castle-2', display: { emoji: '🏯' },
            size: { x: 2, y: 2 },
            chain: { id: 'castle', tier: 2, next: 'castle-3' },
            merge: { recipes: [...STANDARD_MERGE] },
            tap: {
                recipes: [
                    { charge: null, exhaust: null, drops: { gold: 15 } }
                ]
            }
        },
        'castle-3': {
            id: 'castle-3', display: { emoji: '🏛️' },
            size: { x: 3, y: 3 },
            chain: { id: 'castle', tier: 3, next: null },
            tap: {
                recipes: [
                    { charge: null, exhaust: null, drops: { gold: 50, crystalEnergy: 10 } }
                ]
            }
        },

        // Wide items
        'bridge-1': {
            id: 'bridge-1', display: { emoji: '🌉' },
            size: { x: 3, y: 1 },
            chain: { id: 'bridge', tier: 1, next: 'bridge-2' },
            merge: { recipes: [...STANDARD_MERGE] }
        },
        'bridge-2': {
            id: 'bridge-2', display: { emoji: '🌁' },
            size: { x: 4, y: 1 },
            chain: { id: 'bridge', tier: 2, next: 'bridge-3' },
            merge: { recipes: [...STANDARD_MERGE] }
        },
        'bridge-3': {
            id: 'bridge-3', display: { emoji: '🌉' },
            size: { x: 5, y: 1 },
            chain: { id: 'bridge', tier: 3, next: null }
        },

        // Tall items
        'tower-1': {
            id: 'tower-1', display: { emoji: '🗼' },
            size: { x: 1, y: 2 },
            chain: { id: 'tower', tier: 1, next: 'tower-2' },
            merge: { recipes: [...STANDARD_MERGE] },
            charge: {
                max: 200,
                recipes: [
                    { charge: 200, drops: { gold: 10 } }
                ]
            }
        },
        'tower-2': {
            id: 'tower-2', display: { emoji: '🗽' },
            size: { x: 1, y: 3 },
            chain: { id: 'tower', tier: 2, next: 'tower-3' },
            merge: { recipes: [...STANDARD_MERGE] },
            charge: {
                max: 150,
                recipes: [
                    { charge: 150, drops: { gold: 30 } }
                ]
            }
        },
        'tower-3': {
            id: 'tower-3', display: { emoji: '🗿' },
            size: { x: 1, y: 4 },
            chain: { id: 'tower', tier: 3, next: null },
            charge: {
                max: 100,
                recipes: [
                    { charge: 100, drops: { gold: 100, crystalEnergy: 20 } }
                ]
            }
        },

        // ===== SHADE EMITTERS =====
        // Emitters produce shade when covered, drop chests when killed

        'emitter-1': {
            id: 'emitter-1', display: { emoji: '🌑' },
            shade: {
                spawnShade: {
                    amount: 5,              // +5 shade per shade tick
                    enabledInShade: true,
                    minShade: 50,           // needs 50+ shade to be active
                }
            },
            tap: {
                recipes: [
                    { charge: null, exhaust: 1, drops: {} }
                ]
            },
            exhaust: {
                drops: [
                    { 'chest-1': 1 }
                ]
            }
        },
        'emitter-2': {
            id: 'emitter-2', display: { emoji: '🌚' },
            shade: {
                spawnShade: {
                    amount: 10,             // +10 shade per shade tick
                    enabledInShade: true,
                    minShade: 100,          // needs 100+ shade to be active
                }
            },
            tap: {
                recipes: [
                    { charge: null, exhaust: 1, drops: {} }
                ]
            },
            exhaust: {
                drops: [
                    { 'chest-2': 1, 'flower-1': 2 }
                ]
            }
        },
        'emitter-3': {
            id: 'emitter-3', display: { emoji: '🕳️' },
            shade: {
                spawnShade: {
                    amount: 20,             // +20 shade per shade tick
                    enabledInShade: true,
                    minShade: 150,          // needs 150+ shade to be active
                }
            },
            tap: {
                recipes: [
                    { charge: null, exhaust: 1, drops: {} }
                ]
            },
            exhaust: {
                drops: [
                    { 'chest-2': 2, 'flower-2': 1, 'crystal-1': 1 }
                ]
            }
        },
    },
    animations: {
        'merge:collapse': { duration: 18 },
        'merge:spread': { duration: 12 },
        'moving': { duration: 6 },
    }
}

